/**
 * Type id resolution shared by the server registry and the client registry.
 *
 * Column configs carry `type` = lower-kebab id. Converted legacy configs use the kebab form of the
 * legacy name (`dbdropdown`, `check`, `jquery-ui-datepicker`), which manifests map through
 * `legacyTypes`. Order:
 *   1. conditional legacy rules (`when: hasOptions|noOptions`) – e.g. legacy CHECKBOX lists,
 *      which the converter emits as `checkbox` although that id is the boolean element;
 *   2. an element with exactly that id;
 *   3. unconditional legacy aliases.
 */
import type { ElementManifest } from './manifest.js';
import { legacyId, type ElementConfig } from './types.js';

export interface LegacyMapping {
  elementId: string;
  legacyType: string;
  when?: 'hasOptions' | 'noOptions';
  settings?: Record<string, unknown>;
}

export interface Resolution {
  elementId: string;
  /** settings implied by the legacy type (column settings win) */
  implied?: Record<string, unknown>;
  /** legacy type name when resolved through `legacyTypes` */
  legacyType?: string;
}

export class TypeResolver {
  private readonly ids = new Set<string>();
  private readonly plain = new Map<string, LegacyMapping>();
  private readonly conditional = new Map<string, LegacyMapping[]>();

  /** Problems found while adding manifests (duplicates, ambiguous aliases). */
  readonly problems: string[] = [];

  add(manifest: ElementManifest): boolean {
    if (this.ids.has(manifest.id)) {
      this.problems.push(`duplicate element id "${manifest.id}"`);
      return false;
    }
    this.ids.add(manifest.id);
    for (const rule of manifest.legacyTypes) {
      const r = typeof rule === 'string' ? { type: rule } : rule;
      const key = legacyId(r.type);
      const mapping: LegacyMapping = { elementId: manifest.id, legacyType: r.type };
      if ('settings' in r && r.settings) mapping.settings = r.settings;
      if ('when' in r && r.when) {
        mapping.when = r.when;
        const list = this.conditional.get(key) ?? [];
        if (list.some((m) => m.when === mapping.when)) {
          this.problems.push(`legacy type ${r.type} (${r.when}) is claimed by several elements`);
          continue;
        }
        list.push(mapping);
        this.conditional.set(key, list);
      } else {
        const prev = this.plain.get(key);
        if (prev) {
          this.problems.push(`legacy type ${r.type} is claimed by "${prev.elementId}" and "${manifest.id}"`);
          continue;
        }
        this.plain.set(key, mapping);
      }
    }
    return true;
  }

  /** Check alias/id collisions once all manifests are added. */
  verify(): string[] {
    for (const [key, m] of this.plain) {
      if (this.ids.has(key) && key !== m.elementId) {
        this.problems.push(`legacy type ${m.legacyType} of "${m.elementId}" collides with element id "${key}" (use a "when" rule)`);
      }
    }
    return this.problems;
  }

  has(id: string): boolean {
    return this.ids.has(id);
  }

  /** Resolve a type id (or a whole column element config, needed for conditional rules). */
  resolve(typeOrConfig: string | ElementConfig): Resolution | undefined {
    const cfg = typeof typeOrConfig === 'string' ? undefined : typeOrConfig;
    const type = (cfg ? cfg.type : (typeOrConfig as string)).trim();
    const key = legacyId(type);
    if (cfg) {
      const hasOptions = (cfg.options?.length ?? 0) > 0;
      const hit = this.conditional.get(key)?.find((m) => (m.when === 'hasOptions') === hasOptions);
      if (hit) return toResolution(hit);
    }
    if (this.ids.has(type)) return { elementId: type };
    const m = this.plain.get(key);
    return m ? toResolution(m) : undefined;
  }

  /** All legacy mappings (for reports). */
  mappings(): LegacyMapping[] {
    return [...this.plain.values(), ...[...this.conditional.values()].flat()];
  }
}

function toResolution(m: LegacyMapping): Resolution {
  const r: Resolution = { elementId: m.elementId, legacyType: m.legacyType };
  if (m.settings) r.implied = m.settings;
  return r;
}
